import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Cloud as CloudIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { listTrails } from '../../../services/portalApi';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import {
  PortalLoading,
  PortalEmptyState,
  PortalErrorState,
  PortalSectionHeader,
} from '../../../components/portal/PortalStates';
import { formatStatusLabel } from '../../../utils/statusLabel';
import {
  CK,
  FONT_BODY,
  statusChipSx,
  tableHeadRowSx,
  headCellSx,
  rowSx,
  bodyCellSx,
  sectionCardSx,
  listCardSx,
  actionRowSx,
} from '../portalStyles';

const statusIntent = (status) => {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'partial_failure') return 'warning';
  if (status === 'pending' || status === 'deleted') return 'neutral';
  return 'info';
};

const activeTrailStatuses = ['in_progress', 'deleting', 'updating'];

const TrailList = () => {
  const navigate = useNavigate();
  const [trails, setTrails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const fetchTrails = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      const response = await listTrails();
      const data = response.data?.trails || [];
      setTrails(data);
      setError(null);
      return data;
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const response = await listTrails();
      setTrails(response.data?.trails || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchTrails();
  }, [fetchTrails]);

  // Auto-refresh polling when any trail has an active status
  useEffect(() => {
    if (trails.length === 0) return;
    const hasActive = trails.some((t) => activeTrailStatuses.includes(t.status));
    if (hasActive && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const data = await fetchTrails(false);
        const stillActive = data.some((t) => activeTrailStatuses.includes(t.status));
        if (!stillActive && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }, 5000);
    } else if (!hasActive && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [trails, fetchTrails]);

  const hasTrails = trails.length > 0;

  const headerSubtitle = loading || !hasTrails
    ? "Manage AWS CloudTrail trails across your organization's accounts"
    : `${trails.length} trail${trails.length !== 1 ? 's' : ''} configured`;

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
        <PortalPageHeader
          icon={<CloudIcon />}
          title="Trails"
          subtitle={headerSubtitle}
          color="#4698D3"
          action={
            !loading && hasTrails ? (
              <Box sx={{ ...actionRowSx, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={
                    refreshing
                      ? <CircularProgress size={16} color="primary" />
                      : <RefreshIcon />
                  }
                  onClick={handleRefresh}
                  disabled={refreshing}
                  sx={{ px: 2.5 }}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/cloudtrail/trails/create')}
                  sx={{ px: 3, '&.Mui-disabled': { backgroundColor: CK.border, color: CK.muted } }}
                >
                  Create Trail
                </Button>
              </Box>
            ) : undefined
          }
        />
      </motion.div>

      {error && (
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={() => fetchTrails()} />
        </motion.div>
      )}

      {loading ? (
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading trails…" />
          </Paper>
        </motion.div>
      ) : !hasTrails ? (
        !error && (
          <motion.div variants={sectionVariants}>
            <Paper elevation={0} sx={{ ...sectionCardSx, p: { xs: 3, sm: 4 } }}>
              <PortalEmptyState
                icon={<CloudIcon sx={{ fontSize: 28 }} />}
                title="No trails yet"
                description="Create a trail to start capturing AWS API activity across your organization's accounts."
                color={CK.blue}
                action={
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/cloudtrail/trails/create')}
                    sx={{ px: 3 }}
                  >
                    Create Trail
                  </Button>
                }
              />
            </Paper>
          </motion.div>
        )
      ) : (
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={listCardSx}>
            <Box sx={{ p: 3, pb: 2 }}>
              <PortalSectionHeader
                icon={<CloudIcon sx={{ fontSize: 20 }} />}
                title="All trails"
                subtitle="Organization-wide trails and their rollout status"
                color={CK.blue}
                mb={0}
              />
            </Box>
            <TableContainer sx={{ borderTop: `1px solid ${CK.border}` }}>
              <Table>
                <TableHead>
                  <TableRow sx={tableHeadRowSx}>
                    {['Trail Name', 'Status', 'S3 Bucket', 'Multi-Region', 'Accounts', 'Created By', 'Created At'].map(
                      (header) => (
                        <TableCell key={header} sx={headCellSx}>
                          {header}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trails.map((trail, index) => (
                    <TableRow
                      key={trail.id}
                      hover
                      onClick={() => navigate(`/cloudtrail/trails/${trail.id}`)}
                      sx={{
                        ...rowSx(index),
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease, border-color 0.2s ease',
                      }}
                    >
                      <TableCell sx={{ ...bodyCellSx, fontWeight: 600, wordBreak: 'break-word' }}>
                        {trail.name}
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Chip
                          size="small"
                          label={formatStatusLabel(trail.status)}
                          sx={statusChipSx(statusIntent(trail.status))}
                        />
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, wordBreak: 'break-word' }}>
                        {trail.s3BucketName}
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        {trail.isMultiRegion ? 'Yes' : 'No'}
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>
                        {trail.successCount}/{trail.accountCount} succeeded
                        {trail.failedCount > 0 && (
                          <Typography
                            component="span"
                            sx={{ fontFamily: FONT_BODY, color: CK.red, fontSize: '0.8rem', ml: 0.5 }}
                          >
                            ({trail.failedCount} failed)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, wordBreak: 'break-word' }}>
                        {trail.createdByEmail}
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>
                        {new Date(trail.createdAt).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </motion.div>
      )}
    </motion.div>
  );
};

export default TrailList;
